import type { TextField } from '../core/text/textField';
import type { TextSettings } from '../core/text/settings';
import { createRng } from '../core/rng';
import { drawText } from '../core/text/drawText';
import { contoursToPath2D } from '../core/text/flatten';
import type { FieldView } from '../store/studioStore';

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export { drawText };

/**
 * Renders one of the field representations.
 *
 * These are not throwaway debug views — being able to look at the SDF, the gradient
 * and the sample distributions is how you tell whether a technique is misbehaving or
 * the field underneath it is.
 */
export function drawFieldView(
  ctx: Ctx2D,
  field: TextField,
  settings: TextSettings,
  view: FieldView,
  sampleCount: number,
  seed: number,
): void {
  const { width, height } = field;

  switch (view) {
    case 'render':
      drawText(ctx, field, settings);
      return;

    case 'alpha': {
      const img = ctx.createImageData(width, height);
      const d = img.data;
      for (let i = 0, p = 0; i < field.alpha.length; i++, p += 4) {
        const a = field.alpha[i];
        d[p] = d[p + 1] = d[p + 2] = a;
        d[p + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      return;
    }

    case 'sdf': {
      // Blue outside, warm inside, with iso-lines every 24 px so the field's shape and
      // its rate of change are both readable at a glance.
      const img = ctx.createImageData(width, height);
      const d = img.data;
      const outScale = Math.max(1, field.maxOutside);
      const inScale = Math.max(1, field.maxInside);
      for (let i = 0, p = 0; i < field.sdf.length; i++, p += 4) {
        const v = field.sdf[i];
        const band = Math.abs(v) % 24 < 1.6 ? 46 : 0;
        if (v >= 0) {
          const t = 1 - Math.min(1, v / outScale);
          d[p] = 18 + band;
          d[p + 1] = 30 + t * 90 + band;
          d[p + 2] = 60 + t * 160 + band;
        } else {
          const t = 1 - Math.min(1, -v / inScale);
          d[p] = 90 + t * 165 + band;
          d[p + 1] = 40 + t * 110 + band;
          d[p + 2] = 30 + t * 40 + band;
        }
        d[p + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      return;
    }

    case 'gradient': {
      ctx.fillStyle = '#0b0b0e';
      ctx.fillRect(0, 0, width, height);
      const spacing = Math.max(14, Math.round(Math.min(width, height) / 56));
      const len = spacing * 0.44;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let y = spacing / 2; y < height; y += spacing) {
        for (let x = spacing / 2; x < width; x += spacing) {
          const [gx, gy] = field.gradient(x, y);
          if (gx === 0 && gy === 0) continue;
          ctx.moveTo(x - gx * len, y - gy * len);
          ctx.lineTo(x + gx * len, y + gy * len);
        }
      }
      ctx.strokeStyle = 'rgba(216,255,62,0.5)';
      ctx.stroke();
      // Arrow heads, drawn separately so the whole grid is one stroke call each.
      ctx.beginPath();
      for (let y = spacing / 2; y < height; y += spacing) {
        for (let x = spacing / 2; x < width; x += spacing) {
          const [gx, gy] = field.gradient(x, y);
          if (gx === 0 && gy === 0) continue;
          ctx.moveTo(x + gx * len, y + gy * len);
          ctx.arc(x + gx * len, y + gy * len, 1.1, 0, Math.PI * 2);
        }
      }
      ctx.fillStyle = 'rgba(216,255,62,0.9)';
      ctx.fill();
      ctx.globalAlpha = 0.25;
      drawText(ctx, field, settings, '#ffffff');
      ctx.globalAlpha = 1;
      return;
    }

    case 'outline': {
      ctx.strokeStyle = 'rgba(216,255,62,0.85)';
      ctx.lineWidth = 1.25;
      ctx.stroke(contoursToPath2D(field.contours));
      // Vertices, to make the flattening tolerance visible.
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      for (const c of field.contours) {
        for (let i = 0; i < c.points.length; i += 2) {
          ctx.fillRect(c.points[i] - 0.75, c.points[i + 1] - 0.75, 1.5, 1.5);
        }
      }
      return;
    }

    case 'samples': {
      ctx.globalAlpha = 0.16;
      drawText(ctx, field, settings, '#ffffff');
      ctx.globalAlpha = 1;

      const rng = createRng(seed);
      const inner = field.samplePoints(sampleCount, 'poisson', rng);
      ctx.fillStyle = 'rgba(216,255,62,0.85)';
      for (let i = 0; i < inner.length; i += 2) {
        ctx.beginPath();
        ctx.arc(inner[i], inner[i + 1], 1.6, 0, Math.PI * 2);
        ctx.fill();
      }

      const outline = field.sampleOutline(Math.round(sampleCount / 4));
      const normalLength = Math.max(6, Math.min(width, height) / 60);
      ctx.strokeStyle = 'rgba(120,190,255,0.8)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const s of outline) {
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x + s.nx * normalLength, s.y + s.ny * normalLength);
      }
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      for (const s of outline) ctx.fillRect(s.x - 1, s.y - 1, 2, 2);
      return;
    }

    case 'glyphs': {
      ctx.globalAlpha = 0.22;
      drawText(ctx, field, settings, '#ffffff');
      ctx.globalAlpha = 1;
      const rng = createRng(seed);
      for (const g of field.glyphs) {
        if (g.isWhitespace || g.contours.length === 0) continue;
        const hue = Math.floor(rng.next() * 360);
        ctx.strokeStyle = `hsl(${hue} 90% 62% / 0.95)`;
        ctx.lineWidth = 1.25;
        ctx.strokeRect(g.bounds.x, g.bounds.y, g.bounds.width, g.bounds.height);
        ctx.fillStyle = `hsl(${hue} 90% 62% / 0.14)`;
        ctx.fill(contoursToPath2D(g.contours), 'nonzero');
        // Baseline origin marker.
        ctx.fillStyle = `hsl(${hue} 90% 70% / 0.95)`;
        ctx.beginPath();
        ctx.arc(g.originX, g.originY, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
  }
}
